import React, {
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  Alignment,
  Checkbox,
  HTMLSelect,
  Switch,
} from '@blueprintjs/core';
import { useTranslation } from 'react-i18next';
import { ValueLabelPair } from 'data/types';
import './verint-star-gate-target-info.scss';
import * as actions from 'data/accessFunction/accessFunction.actions';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import { RootState } from 'data/root.reducers';
import { getSelectedDeliveryFunctionId } from 'global/global-selectors';
import { TargetModelAccessFunction, TargetModelAfCCCSettings } from 'data/target/target.types';
import { adaptToForm } from 'data/accessFunction/accessFunction.adapter';

const mapState = (state: RootState) => ({
  deliveryFunctionId: getSelectedDeliveryFunctionId(state),
  accessFunction: accessFunctionSelectors.getAccessFunction(state),
});

const mapDispatch = {
  getAccessFunctionWithViewOptions: actions.getAccessFunctionWithViewOptions,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type Props = PropsFromRedux & {
  targetAccessFunction: TargetModelAccessFunction
  accessFunctionSettings: TargetModelAfCCCSettings[],
  isEditing: boolean,
  onUpdate: (afSettings: TargetModelAfCCCSettings[]) => void
};

const VerintStarGateTargetInfo: FunctionComponent<Props> = ({
  targetAccessFunction,
  accessFunctionSettings,
  deliveryFunctionId,
  accessFunction,
  isEditing,
  onUpdate,
  getAccessFunctionWithViewOptions,
}: Props) => {
  const [selectedMonitoringCenter, setSelectedMonitoringCenter] = useState('');
  const [monitoringCenterOptions, setMonitoringCenterOptions] = useState<ValueLabelPair[]>([]);
  const [selectedTerritoryMap, setSelectedTerritoryMap] = useState<{[key: string]: boolean}>({});
  const [territoryList, setTerritoryList] = useState<ValueLabelPair[]>([]);
  const { t } = useTranslation();

  const targetAccessFunctionId = targetAccessFunction.id;
  const allTerritoriesSelected = territoryList.every(({ value }) => selectedTerritoryMap[value] === true);

  useEffect(() => {
    if (targetAccessFunctionId) {
      setSelectedMonitoringCenter('');
      setMonitoringCenterOptions([]);
      setSelectedTerritoryMap({});
      setTerritoryList([]);
      getAccessFunctionWithViewOptions(deliveryFunctionId, targetAccessFunctionId);
    }
  }, [targetAccessFunctionId, deliveryFunctionId, getAccessFunctionWithViewOptions]);

  useEffect(() => {
    if (accessFunction != null) {
      const monitoringCenters: ValueLabelPair[] = [];
      const territories: ValueLabelPair[] = [];

      const accessFunctionForm = adaptToForm(accessFunction);

      accessFunctionForm.monitoringCenters?.forEach(({ mcid, mc }) => {
        if (mcid == null || mc == null) {
          return;
        }

        monitoringCenters.push({
          value: mcid,
          label: `${mcid} - ${mc}`,
        });
      });

      accessFunctionForm.territories?.forEach(({ terrid, terr }) => {
        if (terrid == null || terr == null) {
          return;
        }

        territories.push({
          value: terrid,
          label: terr,
        });
      });

      setMonitoringCenterOptions(monitoringCenters);
      setTerritoryList(territories);

      let accessFunctionSettingMonitoringCenter = monitoringCenters?.[0]?.value ?? '';
      const accessFunctionSettingTerritoryMap: {[key: string]: boolean} = {};
      accessFunctionSettings.forEach(({ options }) => {
        const monitoringCenterId = options.find(({ key }) => key === 'MCID')?.value;
        if (monitoringCenterId != null) {
          accessFunctionSettingMonitoringCenter = monitoringCenterId;
        }

        const territoryId = options.find(({ key }) => key === 'TERRID')?.value;
        if (territoryId === 'ALL') {
          territories.forEach(({ value }) => {
            accessFunctionSettingTerritoryMap[value] = true;
          });

        } else if (territoryId != null) {
          accessFunctionSettingTerritoryMap[territoryId] = true;
        }
      });

      setSelectedMonitoringCenter(accessFunctionSettingMonitoringCenter);
      setSelectedTerritoryMap(accessFunctionSettingTerritoryMap);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessFunction]);

  const getSetting = (territory: string, afId: string): TargetModelAfCCCSettings => {
    const territoryValid = territory !== '';
    const monitoringCenterValid = selectedMonitoringCenter !== '';

    return {
      afids: [afId],
      autowired: false,
      isValid: territoryValid && monitoringCenterValid,
      options: [{
        key: 'TERRID',
        value: territory,
      }, {
        key: 'MCID',
        value: selectedMonitoringCenter,
      }],
    };
  };

  useEffect(() => {
    if ((targetAccessFunctionId == null) ||
        (territoryList.length === 0) ||
        (monitoringCenterOptions.length === 0) ||
        (!isEditing)) {
      return;
    }

    const afSettings: TargetModelAfCCCSettings[] = [];

    if (allTerritoriesSelected) {
      afSettings.push(getSetting('ALL', targetAccessFunctionId));

    } else {
      territoryList.forEach(({ value }) => {
        if (selectedTerritoryMap[value] !== true) {
          return;
        }

        afSettings.push(getSetting(value, targetAccessFunctionId));
      });
    }

    onUpdate(afSettings);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonitoringCenter, selectedTerritoryMap]);

  const handleMonitoringCenterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMonitoringCenter(event.target.value);
  };

  const handleSelectAllTerritoriesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedTerritories = {
      ...selectedTerritoryMap,
    };

    territoryList.forEach(({ value }) => {
      selectedTerritories[value] = event.target.checked;
    });

    setSelectedTerritoryMap(selectedTerritories);
  };

  const handleTerritoryListSelectedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const territoryId = event.target.id;
    const selected = event.target.checked;

    const selectedTerritories = {
      ...selectedTerritoryMap,
      [territoryId]: selected,
    };

    setSelectedTerritoryMap(selectedTerritories);
  };

  const renderTerritoryList = () => {
    return territoryList.map(({ label, value }) => {
      const checked = selectedTerritoryMap[value] === true;
      return (
        <div key={value} className="access-function-item">
          <Checkbox
            id={value}
            label={label}
            checked={checked}
            onChange={handleTerritoryListSelectedChange}
            disabled={isEditing === false}
          />
        </div>
      );
    }) || null;
  };

  return (
    <div className="verint-star-gate-target-info" data-testid="VerintStarGateTargetInfo">
      <div className="header">
        {t('warrant.target.form.verint.targetInformation')}
      </div>
      <div className="monitoring-center">
        {t('warrant.target.form.verint.monitoringCenter')}
        <HTMLSelect
          autoComplete="off"
          autoCorrect="off"
          onChange={handleMonitoringCenterChange}
          options={monitoringCenterOptions}
          value={selectedMonitoringCenter}
          fill
          disabled={isEditing === false}
        />
      </div>
      <div className="territories">
        <div className="territories-header">
          <div className="title">
            {t('warrant.target.form.verint.territories')}
          </div>
          <div>
            <Switch
              label={t('warrant.target.form.selectAll')}
              onChange={handleSelectAllTerritoriesChange}
              checked={allTerritoriesSelected}
              alignIndicator={Alignment.RIGHT}
              disabled={isEditing === false}
              inline
            />
          </div>
        </div>
        <div className="list">
          {renderTerritoryList()}
        </div>
      </div>

    </div>
  );
};

export default connector(VerintStarGateTargetInfo);
